import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { adminFetch } from '@/lib/apiClient';
import { idempotencyKeyFor, releaseIdempotencyKey } from '@/lib/http-core';
import { errorFromResponse } from '@/lib/errors';
import type { AdminRole } from '@/lib/api-admin';

/**
 * QA-510 택배사(배송 업체) 마스터 — order-service `/admin/v1/shipping/couriers`(고도몰 [배송 업체 관리] 56개 이관).
 * 목록은 미사용 포함·순서대로. "사용" 중 맨 위 업체가 송장 입력 기본값(고도몰과 같음).
 */
export interface ShippingCourier {
  courierCode: string;
  nameKo: string;
  nameEn: string | null;
  /** 배송조회 URL — 송장번호 자리 {trackingNo}. 비면 배송조회 없음. */
  trackingUrlTemplate: string | null;
  active: boolean;
  sortOrder: number;
  /** 고도몰 배송업체번호. 직접 추가한 업체는 null. */
  godoSno: number | null;
  /** 직접 추가한 업체 — 이것만(주문에 안 쓰였으면) 삭제할 수 있다. */
  custom: boolean;
  updatedBy: string | null;
  updatedAt: string | null;
}

export interface CourierInput {
  nameKo: string;
  nameEn: string;
  trackingUrlTemplate: string;
  active: boolean;
}

/** 조회 권한 — 메뉴·화면 진입(BE GET 과 같음). */
export const COURIER_READ_ROLES: ReadonlyArray<AdminRole> = ['SUPER_ADMIN', 'OPS_ADMIN', 'CS_AGENT', 'FULFILLMENT_AGENT', 'MD_MANAGER', 'AUDITOR'];

/** 변경 권한 — BE 와 같다(조회는 주문·출고 담당도). */
export const COURIER_WRITE_ROLES: ReadonlyArray<AdminRole> = ['SUPER_ADMIN', 'OPS_ADMIN'];

const BASE = '/admin/v1/shipping/couriers';
export const shippingCouriersQueryKey = ['admin', 'shipping-couriers'] as const;

export const useShippingCouriersQuery = (enabled = true) =>
  useQuery<ShippingCourier[]>({
    queryKey: shippingCouriersQueryKey,
    enabled,
    queryFn: async () => {
      const res = await adminFetch(BASE);
      if (!res.ok) throw await errorFromResponse(res);
      const env = (await res.json()) as { data?: { items?: ShippingCourier[] } | null };
      return [...(env.data?.items ?? [])].sort((a, b) => a.sortOrder - b.sortOrder);
    },
    staleTime: 5 * 60 * 1000,
  });

/** 송장 입력용 — "사용" 업체만 순서대로. 첫 번째가 기본값. */
export const useActiveCouriers = () => {
  const query = useShippingCouriersQuery();
  return { ...query, couriers: (query.data ?? []).filter((c) => c.active) };
};

const send = async (method: 'POST' | 'PUT' | 'DELETE', path: string, body?: unknown) => {
  const keyBody = body ?? { at: Date.now() };
  const res = await adminFetch(path, {
    method,
    headers: { 'Content-Type': 'application/json', 'Idempotency-Key': idempotencyKeyFor(method, path, keyBody) },
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });
  if (!res.ok) throw await errorFromResponse(res);
  // 같은 값으로 되돌려 저장하는 경우가 첫 응답 재생으로 묻히지 않게 키를 푼다.
  releaseIdempotencyKey(method, path, keyBody);
};

const toBody = (input: CourierInput) => ({
  nameKo: input.nameKo.trim(),
  nameEn: input.nameEn.trim() || null,
  trackingUrlTemplate: input.trackingUrlTemplate.trim() || null,
  active: input.active,
});

export const useCourierMutations = () => {
  const qc = useQueryClient();
  const onSuccess = () => qc.invalidateQueries({ queryKey: shippingCouriersQueryKey });
  return {
    create: useMutation({
      mutationFn: ({ courierCode, input }: { courierCode: string; input: CourierInput }) =>
        send('POST', BASE, { courierCode: courierCode.trim().toUpperCase(), ...toBody(input) }),
      onSuccess,
    }),
    update: useMutation({
      mutationFn: ({ courierCode, input }: { courierCode: string; input: CourierInput }) =>
        send('PUT', `${BASE}/${encodeURIComponent(courierCode)}`, toBody(input)),
      onSuccess,
    }),
    reorder: useMutation({
      mutationFn: (courierCodes: string[]) => send('PUT', `${BASE}/sort-order`, { courierCodes }),
      onSuccess,
    }),
    remove: useMutation({
      mutationFn: (courierCode: string) => send('DELETE', `${BASE}/${encodeURIComponent(courierCode)}`),
      onSuccess,
    }),
  };
};

/** 영문 대문자·숫자·_ 2~20자(BE 와 같음). */
export const isValidCourierCode = (code: string) => /^[A-Z0-9_]{2,20}$/.test(code.trim().toUpperCase());

/** http(s) 주소이고 {trackingNo}(또는 고도몰 __INVOICENO__)가 있어야 한다. 비면 통과(배송조회 없음). */
export const isValidTrackingUrl = (url: string) => {
  const v = url.trim();
  if (v === '') return true;
  return /^https?:\/\/\S+$/i.test(v) && (v.includes('{trackingNo}') || v.includes('__INVOICENO__'));
};
